import { appendAll, clone, element, toArray } from "../util/Utils";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { SortingInput } from "./SortingInput";
import { SubmitOnChangeListener } from "../filter/SubmitOnChange";
import { SmartSorting } from "./SmartSorting";
import { SortingSubmitAction } from "./SortingSubmitAction";
import { dereference, updateStatus } from "../tracking/FeatureTracking";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { OverflowBox } from "../overflowBox/OverflowBox";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { events } from "../events";
import { mergeLabel } from "../tracking/GlobalTracking";
import { isDesktop } from "../util/Breakpoint";
import { FilterTitles } from "../filter/FilterTitles";

const SORTING_FORM = "ts_sortingForm";
const SELECTOR_TEMPLATE_SORTING_FORM = `#heureka_desktopSorting--initialization form.${SORTING_FORM}`;
const SELECTOR_ACTIVE_SORTING_FORM_ANCHOR = "#heureka_desktopSorting";
const SELECTOR_SORTING_RELEVANT_VALUES = ".heureka_sorting__relevantValues";

export class SortingForm {
  static readonly factory = HeurekaElementFactory.byElement("form", SORTING_FORM, SortingForm);

  /*               */
  constructor(
    readonly form: HTMLFormElement,
    private readonly relevantValues = element(SELECTOR_SORTING_RELEVANT_VALUES, form),
  ) {}

  /*                  */

  static template(rootElement?: ParentNode | null) {
    return SortingForm.factory.pick(SELECTOR_TEMPLATE_SORTING_FORM, rootElement);
  }

  /*               */

  static register() {
    events.onFilterSectionLoaded(SortingForm.initAll);
  }

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  static getCurrentSortValue(): string | undefined {
    /*                                                                         */
    /*                                                                               */
    if (isDesktop()) {
      const activeSelect = document.querySelector<HTMLSelectElement>(
        `${SELECTOR_ACTIVE_SORTING_FORM_ANCHOR} select[name='sortiertnach']`,
      );
      if (activeSelect?.value) return activeSelect.value;
    } else {
      /*                                                                */
      const filterTitles = FilterTitles.template()?.elem;
      if (filterTitles) {
        SortingForm.factory.pick(undefined, filterTitles);
        const radio = SortingForm.factory
          .pick(undefined, filterTitles)
          ?.form?.querySelector<HTMLInputElement>("input[name='sortiertnach']:checked");
        if (radio?.value) return radio.value;
      }
    }

    return undefined;
  }

  static initAll(event?: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    SortingForm.factory.forEach((form) => form.init(), rootElement);
  }

  protected init() {
    this.form.addEventListener("submit", SortingForm.interceptSubmit);
    const nestedOverflowBox = OverflowBox.factory.pick(undefined, this.form);
    if (nestedOverflowBox) {
      this.form.addEventListener("change", nestedOverflowBox.resetScrollListener());
    }
    SortingInput.initAll({}, this.form);
    SubmitOnChangeListener.on(this.form, SubmitOnChangeListener.submitOnChange(this.submit.bind(this)));
    /*                                                        */
    this.additionalInputs = this.isCategoryPathSelected ? undefined : SmartSorting.template()?.relevantValues;
    return this;
  }

  /*                       */

  get baseRule(): string {
    return this.form.elements["rule"]?.value || "";
  }

  get isCategoryPathSelected(): boolean {
    return this.baseRule.includes("kategorien");
  }

  get additionalInputs(): HTMLInputElement[] | undefined {
    return (this.relevantValues && toArray(this.relevantValues.children)) || undefined;
  }

  set additionalInputs(values: HTMLInputElement[] | undefined) {
    appendAll(values, this.relevantValues, true);
  }

  get hasAdditionalInputs(): boolean {
    return !!this.relevantValues?.children.length;
  }

  mergeLabel<T extends keyof TrackingLabels>(label: T, value: TrackingLabels[T]) {
    mergeLabel(this.form, label, value);
  }

  submit(submitter: HTMLElement = this.form) {
    const { hasAdditionalInputs: couldSmartSort, form } = this;
    const valueSubmitter = submitter as HTMLInputElement | HTMLSelectElement | HTMLFormElement;
    const shouldSmartSort =
      couldSmartSort &&
      (submitter instanceof HTMLFormElement ||
        submitter instanceof HTMLSelectElement ||
        submitter instanceof HTMLInputElement ||
        (submitter.classList.contains("hc_facetValueInput") && submitter.hasAttribute("value"))) &&
      (valueSubmitter.value === "preis-aufsteigend" || valueSubmitter.value === "bewertung");
    if (shouldSmartSort) {
      form.dataset.tsFeatureAction = "add";
      form.dataset.tsFeatureFilterMethod = "sorting_change";
      this.additionalInputs?.map(dereference).forEach(SortingForm.setChecked);
    }
    form.dispatchEvent(
      /*                                                                                                */
      /*                                                                         */
      /*               */
      SortingSubmitAction.sortingSubmitEvent(submitter, shouldSmartSort),
    );
  }

  clone() {
    return SortingForm.factory.create(clone(this.form)).init();
  }

  /*                 */

  private static interceptSubmit(event: Event) {
    if (event.defaultPrevented) {
      return;
    }

    SortingForm.factory.declare(event.target)?.submit();

    /*                         */
    event.preventDefault();
  }

  /*       */

  private static setChecked(elem: HTMLInputElement) {
    if (!elem.defaultChecked) {
      updateStatus(elem, "preselected", 2);
    }
    elem.checked = true;
  }
}
